import { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap, useMapEvents } from 'react-leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import { RootState } from '../../store/store';
import { useTheme } from '../../hooks/useTheme';
import { getBasemapThemeClassName } from '../../styles/themes';
import { addWaypoint, setHomeBase } from '../../store/slices/missionPlannerSlice';
import { WaypointMarkerLayer } from './WaypointMarkerLayer';
import { RouteSegments } from './RouteSegments';
import { PlannerHomeBaseMarker } from './PlannerHomeBaseMarker';
import { effectiveHomeBase } from '../../utils/route';
import { ShapeLayer } from './ShapeLayer';
import { ShapeEditor } from './ShapeEditor';
import { ShapeDrawHandler } from './ShapeDrawHandler';
import { GeofenceLayer } from './GeofenceLayer';
import { useGeofenceViolations } from '../../hooks/useGeofenceViolations';
import { DeviceLocationCenter } from '../Map/DeviceLocationCenter';
import { MapSearchField } from '../Map/MapSearchField';
import { useInitialMapView } from '../../hooks/useInitialMapView';
import { MapViewMemory } from '../Map/MapViewMemory';
import { WORLD_MAP_OPTIONS, WorldBasemap } from '../Map/WorldBasemap';
import { SearchResultMarker } from '../Map/SearchResultMarker';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { tutorialTarget } from '../../tutorial/tours';
import 'leaflet/dist/leaflet.css';

// ---------------------------------------------------------------------------
// MapThemeUpdater — invalidate size on theme flip (existing DroneMap idiom).
// ---------------------------------------------------------------------------
const MapThemeUpdater: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [isDarkMode, map]);
  return null;
};

// ---------------------------------------------------------------------------
// MapSizeUpdater — reflow tiles when the left panel collapses/expands and the
// map's container width changes (issue #343). Same invalidateSize idiom.
// ---------------------------------------------------------------------------
const MapSizeUpdater: React.FC<{ panelCollapsed: boolean }> = ({
  panelCollapsed,
}) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [panelCollapsed, map]);
  return null;
};

// ---------------------------------------------------------------------------
// MapClickHandler — routes map clicks by placement mode: append a waypoint
// while 'placing', set the home base while 'placing-home'.
// Must live inside MapContainer to use useMapEvents.
// ---------------------------------------------------------------------------
const MapClickHandler: React.FC = () => {
  const dispatch = useDispatch();
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );
  const activeShapeTool = useSelector(
    (state: RootState) => state.missionPlanner.activeShapeTool
  );

  useMapEvents({
    click(e) {
      if (placementMode === 'placing') {
        dispatch(addWaypoint({ lat: e.latlng.lat, lng: e.latlng.lng }));
      } else if (placementMode === 'placing-home') {
        dispatch(setHomeBase({ lat: e.latlng.lat, lng: e.latlng.lng }));
      }
    },
    // Long-press (touch) / right-click (desktop): drop a waypoint directly,
    // without first toggling placement mode (issue #356). Yields to the
    // home-base and shape-draw gestures, and ignores presses that bubbled up
    // from a shape or marker (propagatedFrom set) so those aren't hijacked.
    contextmenu(e) {
      if (e.propagatedFrom) {
        return;
      }
      if (activeShapeTool !== null || placementMode === 'placing-home') {
        return;
      }
      e.originalEvent?.preventDefault();
      dispatch(addWaypoint({ lat: e.latlng.lat, lng: e.latlng.lng }));
    },
  });

  return null;
};

// ---------------------------------------------------------------------------
// PlacementBanner — floating instruction while placing (role="status").
// ---------------------------------------------------------------------------
const PlacementBanner: React.FC = () => {
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );

  if (placementMode === 'idle') {
    return null;
  }

  const message =
    placementMode === 'placing-home'
      ? 'Click the map to set the home base. Press Escape to cancel.'
      : 'Click the map to add waypoints. Press Escape when done.';

  return (
    <Paper
      role="status"
      aria-live="polite"
      elevation={4}
      sx={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        px: 3,
        py: 1.5,
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
        borderRadius: 2,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Typography variant="body2" fontWeight="bold">
        {message}
      </Typography>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// PlannerMapLayers — everything that lives *inside* the MapContainer.
// Exported so functional tests can mount the real layers in a test map and
// fire genuine Leaflet events (click-to-place, drag) against them.
// ---------------------------------------------------------------------------
export const PlannerMapLayers: React.FC<{
  /**
   * True when the map opened on a remembered viewport (#441). Passed down
   * rather than read here: MapContainer's own setView fires `moveend` during
   * construction, so by the time these layers mount the store already holds a
   * viewport — reading it here would report "remembered" on the very first
   * load and suppress the device-location centre that #401 added.
   */
  viewRemembered?: boolean;
}> = ({ viewRemembered = false }) => {
  const { isDarkMode } = useTheme();
  const homeBase = useSelector(
    (state: RootState) => state.missionPlanner.draft.homeBase
  );
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  const shapes = useSelector(
    (state: RootState) => state.missionPlanner.draft.shapes
  );
  const panelCollapsed = useSelector(
    (state: RootState) => state.missionPlanner.panelCollapsed
  );
  // Only a custom home is flagged here; a default home sits on waypoint 1,
  // which carries its own marking already (#451).
  const { homeBaseOutside } = useGeofenceViolations();

  // The home pin sits on the custom home if set, otherwise the start point
  // (waypoint 1) — the default home the loop closes back to (issue #342).
  const home = effectiveHomeBase(waypoints, homeBase);

  return (
    <>
      <MapThemeUpdater isDarkMode={isDarkMode} />
      <MapViewMemory />

      {/* Where a location search landed (#523). */}
      <SearchResultMarker />
      <MapSizeUpdater panelCollapsed={panelCollapsed} />
      <MapClickHandler />
      <ShapeDrawHandler />

      {/* Center on the operator's device, but only while the draft is empty —
          anything already drawn is the better center (issue #401), and so is a
          viewport the operator is being returned to (#441). */}
      <DeviceLocationCenter
        enabled={
          !viewRemembered &&
          waypoints.length === 0 &&
          shapes.length === 0 &&
          homeBase === null
        }
      />

      {/* One world, filling the map, at every size (#576). */}
      <WorldBasemap />

      {/* The drone's inclusion geofence, under everything the operator draws —
          it is a constraint on the route, not part of it (#451). */}
      <GeofenceLayer />

      {/* Coverage-area shapes, below the route, with editor for the selected one. */}
      {shapes.map((shape) => (
        <ShapeLayer key={shape.id} shape={shape} />
      ))}
      <ShapeEditor />

      {/* Route legs with per-segment distance labels + hover detail. The
          loop-closing leg back to home is one of them (#404). */}
      <RouteSegments />

      {/* Numbered, draggable waypoint markers (start green → end red),
          viewport-culled above CULL_THRESHOLD so large routes stay fluid. */}
      <WaypointMarkerLayer />

      {/* Home pin — custom home, or the default start point (waypoint 1). */}
      {home && (
        <PlannerHomeBaseMarker
          position={home}
          isDefault={homeBase === null}
          outsideGeofence={homeBaseOutside}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// PlannerMap — authoring map: click-to-place waypoints + route polyline.
// ---------------------------------------------------------------------------
export const PlannerMap: React.FC = () => {
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );
  const activeShapeTool = useSelector(
    (state: RootState) => state.missionPlanner.activeShapeTool
  );
  const drawing = placementMode !== 'idle' || activeShapeTool !== null;
  const mapRef = useRef<L.Map | null>(null);
  // Loading state: overlay a spinner until Leaflet reports the map is ready.
  const [ready, setReady] = useState(false);
  // Where this map opens: the remembered viewport if the operator has moved a
  // map before, otherwise the default (#441).
  const initialView = useInitialMapView();
  // Read here as well as in PlannerMapLayers: the basemap filter has to hang
  // off this wrapper, which is outside the MapContainer (#440).
  const { isDarkMode } = useTheme();


  return (
    <Box
      // The dark-basemap filter lives here, not on the tile layer: a layer's
      // className is write-once, so it used to survive the switch to light mode
      // and blank the map (#440).
      className={getBasemapThemeClassName(isDarkMode)}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        cursor: drawing ? 'crosshair' : undefined,
      }}
      {...tutorialTarget('planner-map')}
    >
      <PlacementBanner />

      {/* Jump-to-location search — same placement as the fleet map. */}
      {/* Positioned by the parent since #518 — see MapSearchField. */}
      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
        <MapSearchField mapRef={mapRef} />
      </Box>

      {/* Loading overlay until the map is ready. */}
      {!ready && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1100,
            bgcolor: 'background.default',
          }}
        >
          <LoadingSpinner message="Loading map…" />
        </Box>
      )}

      <MapContainer
        center={initialView.center}
        zoom={initialView.zoom}
        style={{ width: '100%', height: '100%' }}
        ref={mapRef}
        whenReady={() => setReady(true)}
        {...WORLD_MAP_OPTIONS}
      >
        <PlannerMapLayers viewRemembered={initialView.remembered} />
      </MapContainer>
    </Box>
  );
};
